<template>
	<section class="teacher-resource-section" :aria-label="t(`${I18N_NS}.teacherResource.ariaLabel`)">
		<PageHeader v-model="searchKeyword" @search="handleSearch" />
		<PcSearchModal v-model:show="showSearchModal" trigger-position="master_fortune_search" />

		<div class="teacher-resource-section__container">
			<h2 class="teacher-resource-section__title">{{ t(`${I18N_NS}.teacherResource.title`) }}</h2>

			<TeacherCardsSkeleton v-if="showSkeleton" variant="carousel" :show-title="false" :count="4" />
			<div v-else-if="!teacherList.length" class="teacher-resource-section__status">
				{{ t(`${I18N_NS}.teacherResource.empty`) }}
			</div>

			<div v-else class="teacher-resource-section__carousel-wrap">
				<div class="teacher-resource-section__carousel">
					<div class="teacher-resource-section__viewport">
						<div
							ref="listRef"
							class="teacher-resource-section__list"
							:class="{ 'teacher-resource-section__list--dragging': isDragging }"
							@scroll="handleScroll"
							@pointerdown="onPointerDown"
							@pointermove="onPointerMove"
							@pointerup="onPointerUp"
							@pointercancel="onPointerUp"
							@dragstart.prevent
						>
							<article
								v-for="teacher in teacherList"
								:key="teacher.teacher_uid || teacher.id"
								:ref="el => setCardRef(el, teacher)"
								class="teacher-resource-card"
								@click="handleDetailClick(teacher)"
							>
								<div class="teacher-resource-card__media">
									<img
										class="teacher-resource-card__avatar"
										:src="teacher?.avatar"
										:alt="teacher?.name || t(`${I18N_NS}.teacherResource.avatarAlt`)"
										draggable="false"
									/>
									<div class="teacher-resource-card__meta-group">
										<div class="teacher-resource-card__meta teacher-resource-card__meta--score">
											<img :src="starIcon" alt="" aria-hidden="true" draggable="false" />
											<span>{{ teacher?.displayScore }}</span>
										</div>
										<div class="teacher-resource-card__meta teacher-resource-card__meta--like">
											<img :src="likeIcon" alt="" aria-hidden="true" draggable="false" />
											<span>{{ teacher?.displayLikes }}</span>
										</div>
									</div>
								</div>

								<!-- <button
									v-if="teacher.showChatBubble"
									class="teacher-resource-card__chat"
									type="button"
									:aria-label="t(`${I18N_NS}.teacherResource.chatAria`)"
									@click.stop="handleConsultClick(teacher)"
								>
									<img :src="chatIcon" alt="" aria-hidden="true" />
								</button> -->

								<div class="teacher-resource-card__content">
									<h3 class="teacher-resource-card__name">{{ teacher?.name }}</h3>
									<div class="teacher-resource-card__badges">
										<span
											v-for="(badge, badgeIndex) in (teacher?.highlightBadges || []).slice(0, 3)"
											:key="badge?.text || `${badgeIndex}`"
											class="teacher-resource-card__badge"
											:style="getBadgeStyle(badge, badgeIndex)"
										>
											{{ badge?.text || "" }}
										</span>
									</div>
									<div class="teacher-resource-card__summary-row">
										<p class="teacher-resource-card__summary">
											{{ teacher?.summaryText }}
										</p>
										<span class="teacher-resource-card__summary-arrow" aria-hidden="true"></span>
									</div>
									<button
										class="teacher-resource-card__button"
										type="button"
										@click.stop="handleCardAction(teacher)"
									>
										{{ teacher?.actionText }}
									</button>
								</div>
							</article>
						</div>
					</div>
				</div>

				<div
					v-if="showDots"
					class="teacher-resource-section__dots"
					role="tablist"
					:aria-label="t(`${I18N_NS}.teacherResource.dotsAria`)"
				>
					<button
						v-for="index in dotCount"
						:key="index - 1"
						type="button"
						class="teacher-resource-section__dot"
						:class="{ 'teacher-resource-section__dot--active': activeDot === index - 1 }"
						:aria-label="t(`${I18N_NS}.teacherResource.pageAria`, { index })"
						:aria-selected="activeDot === index - 1"
						role="tab"
						@click="scrollToDot(index - 1)"
					/>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { computed, inject, ref, onBeforeUnmount, onMounted, nextTick, watch } from "vue";
import { getTeacherAskRecommendList } from "~/api/api";
import { useMasterFortunePcPageData } from "@variants/seer/master-fortune/composables/useMasterFortunePageData";
import { logEvent } from "~/utils/firebase";
import { getChannel } from "~/utils/getChannel";
import PageHeader from "../page-header/index.vue";
import PcSearchModal from "~/components/pc/header/pc-search-modal.vue";
import TeacherCardsSkeleton from "../teacher-cards-skeleton/index.vue";
import { useI18n } from "~/composables/useI18n";

const I18N_NS = "masterFortune";
import likeIcon from "../../../images/like.png";
import starIcon from "../../../images/star.png";
import chatIcon from "./images/chat.png";
import {
	getConsultationCountBucket,
	getMentorTypeByAreaType,
	getPriceBucket,
	normalizeMentorRating,
	useMentorListTracking,
} from "@variants/seer/master-fortune/composables/useMentorListTracking";

const RESOURCE_SIZE = 12;
const TEACHER_DETAIL_AREA_TYPE = 10;
const TEACHER_UID_STORAGE_KEY = "pending_chat_teacher_uid";

const { t, trackTranslationDeps } = useI18n();
const { track } = useMentorListTracking({
	deviceType: "pc",
	mentorType: "normal",
});

const openLoginPopup = inject("openLoginPopup", null);
const pageData = useMasterFortunePcPageData();
const searchKeyword = ref("");
const showSearchModal = ref(false);
const listRef = ref(null);
const activeDot = ref(0);
const visibleCardCount = ref(4);
const teacherListData = ref([]);
const loading = ref(true);
const isDragging = ref(false);
const cardRefs = new Map();
const impressedMentorIds = new Set();
const impressionTimers = new Map();
let cardObserver = null;
let dragStartX = 0;
let dragStartScrollLeft = 0;
let dragMoved = false;
let activePointerId = null;

const teacherList = computed(() => {
	trackTranslationDeps();
	return (teacherListData.value || []).map(formatTeacherResource);
});
const showSkeleton = computed(() => loading.value && !teacherList.value.length);

watch(
	teacherList,
	list => {
		console.log("[推荐导师teacher-resource-section] teacherList:", list);
		console.log("[推荐导师teacher-resource-section] teacherListData:", teacherListData.value);
	},
	{ immediate: true, deep: true }
);

const pageStarts = computed(() => {
	const total = teacherList.value.length;
	const visible = visibleCardCount.value;
	if (total <= visible) return [0];

	const maxStart = total - visible;
	const starts = [0];
	let next = visible;
	while (next < maxStart) {
		starts.push(next);
		next += visible;
	}
	if (starts[starts.length - 1] !== maxStart) {
		starts.push(maxStart);
	}
	return starts;
});

const dotCount = computed(() => pageStarts.value.length);
const showDots = computed(() => dotCount.value > 1);

async function loadTeachers() {
	loading.value = true;
	try {
		const res = await getTeacherAskRecommendList({
			page: 1,
			size: RESOURCE_SIZE,
		});
		teacherListData.value = res?.data || [];
	} catch (error) {
		console.error("getTeacherAskRecommendList error:", error);
	} finally {
		loading.value = false;
	}
}

function isDetailOnlyTeacher(teacher) {
	return Number(teacher?.area_type) === TEACHER_DETAIL_AREA_TYPE;
}

function formatTeacherResource(teacher) {
	const labels = normalizeLabels(teacher?.label);
	const detailOnly = isDetailOnlyTeacher(teacher);

	return {
		...teacher,
		teacher_uid: getTeacherUid(teacher),
		avatar: teacher?.avatar || "",
		name: teacher?.nickname || t(`${I18N_NS}.teacherResource.defaultName`),
		displayScore: teacher?.score || "5.0",
		displayLikes: teacher?.answers ?? 0,
		highlightBadges: labels.length ? labels : getDefaultBadges(),
		summaryText:
			teacher?.introduce || teacher?.brief_intro || t(`${I18N_NS}.teacherResource.defaultSummary`),
		showChatBubble: !detailOnly,
		actionText: detailOnly
			? t(`${I18N_NS}.teacherResource.viewDetail`)
			: t(`${I18N_NS}.teacherResource.consultNow`),
	};
}

function getTeacherUid(teacher) {
	return teacher?.teacher_uid || teacher?.uid || teacher?.id || "";
}

function getDefaultBadges() {
	trackTranslationDeps();
	return [0, 1].map(index => ({
		text: t(`${I18N_NS}.teacherResource.defaultTags.${index}`),
		textColor: "",
		bgColor: "",
		_index: index,
	}));
}

function normalizeLabels(labels) {
	if (!Array.isArray(labels)) return [];
	return labels
		.map(item => ({
			text: item?.text || "",
			textColor: numberColorToCss(item?.text_color),
			bgColor: numberColorToCss(item?.color),
		}))
		.filter(item => item.text);
}

function numberColorToCss(value) {
	const num = Number(value);
	if (!Number.isFinite(num) || num < 0) return "";
	const hex = num.toString(16).padStart(8, "0");
	const alpha = parseInt(hex.slice(0, 2), 16) / 255;
	const r = parseInt(hex.slice(2, 4), 16);
	const g = parseInt(hex.slice(4, 6), 16);
	const b = parseInt(hex.slice(6, 8), 16);

	if (alpha >= 0.999) {
		return `#${hex.slice(2)}`;
	}
	return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
}

function getBadgeStyle(badge, badgeIndex) {
	const fallbackStyles = [
		{ background: "#fff3f0", color: "#f05221" },
		{ background: "#fff9ea", color: "#f2af19" },
		{ background: "#fff9ea", color: "#f2af19" },
	];
	const fallback = fallbackStyles[badgeIndex] || {
		background: "#fff9ea",
		color: "#f2af19",
	};

	return {
		background: badge?.bgColor || fallback.background,
		color: badge?.textColor || fallback.color,
	};
}

function handleSearch() {
	showSearchModal.value = true;
}

function handleCardAction(teacher) {
	if (dragMoved) {
		dragMoved = false;
		return;
	}
	const teacherUid = getTeacherUid(teacher);
	if (!teacherUid) return;
	if (isDetailOnlyTeacher(teacher)) {
		toTeacherDetail(teacherUid);
		return;
	}
	handleConsultClick(teacher);
}

function handleConsultClick(teacher) {
	const teacherUid = getTeacherUid(teacher);
	if (!teacherUid) return;
	track("mentor_consult_click", {
		mentor_id: teacherUid,
		mentor_type: getMentorTypeByAreaType(teacher?.area_type),
		section_id: "recommended_mentor",
		price_bucket: getPriceBucket(teacher?.displayPrice),
	});
	toChat(teacherUid);
}

function getScrollStep() {
	const listEl = listRef.value;
	if (!listEl) return 0;

	const firstCard = listEl.querySelector(".teacher-resource-card");
	if (!firstCard) return 0;

	const styles = window.getComputedStyle(listEl);
	const gap = Number.parseFloat(styles.columnGap || styles.gap || "0") || 16;
	return firstCard.offsetWidth + gap;
}

function syncVisibleCardCount() {
	if (!import.meta.client) return;
	if (window.matchMedia("(max-width: 480px)").matches) {
		visibleCardCount.value = 1;
	} else if (window.matchMedia("(max-width: 900px)").matches) {
		visibleCardCount.value = 2;
	} else if (window.matchMedia("(max-width: 1024px)").matches) {
		visibleCardCount.value = 3;
	} else {
		visibleCardCount.value = 4;
	}
}

function handleScroll() {
	const listEl = listRef.value;
	const step = getScrollStep();
	const starts = pageStarts.value;
	if (!listEl || !step || !starts.length) return;

	const scrollLeft = listEl.scrollLeft;
	let closest = 0;
	let minDist = Infinity;
	starts.forEach((start, index) => {
		const dist = Math.abs(scrollLeft - start * step);
		if (dist < minDist) {
			minDist = dist;
			closest = index;
		}
	});
	activeDot.value = closest;
}

function scrollToDot(index) {
	const listEl = listRef.value;
	const step = getScrollStep();
	const start = pageStarts.value[index];
	if (!listEl || !step || start == null) return;

	const prevDot = activeDot.value;
	activeDot.value = index;
	listEl.scrollTo({
		left: start * step,
		behavior: "smooth",
	});
	track("recommended_mentor_carousel_click", {
		direction: index > prevDot ? "next" : "prev",
		current_index: index + 1,
	});
}

function handleResize() {
	syncVisibleCardCount();
	nextTick(() => {
		handleScroll();
	});
}

function onPointerDown(event) {
	const listEl = listRef.value;
	if (!listEl || event.button !== 0) return;
	// 按钮等可交互元素不走拖拽，保留点击
	if (event.target?.closest?.("button, a, input, textarea, select")) return;

	activePointerId = event.pointerId;
	dragStartX = event.clientX;
	dragStartScrollLeft = listEl.scrollLeft;
	dragMoved = false;
	isDragging.value = false;
}

function onPointerMove(event) {
	const listEl = listRef.value;
	if (!listEl || activePointerId !== event.pointerId) return;

	const distance = event.clientX - dragStartX;
	if (!dragMoved && Math.abs(distance) < 4) return;

	if (!dragMoved) {
		listEl.setPointerCapture(event.pointerId);
	}
	dragMoved = true;
	isDragging.value = true;
	listEl.scrollLeft = dragStartScrollLeft - distance;
	event.preventDefault();
}

function onPointerUp(event) {
	const listEl = listRef.value;
	if (!listEl || activePointerId !== event.pointerId) return;

	if (listEl.hasPointerCapture(event.pointerId)) {
		listEl.releasePointerCapture(event.pointerId);
	}
	activePointerId = null;
	isDragging.value = false;
}

function handleDetailClick(teacher) {
	if (dragMoved) {
		dragMoved = false;
		return;
	}
	const teacherUid = getTeacherUid(teacher);
	if (!teacherUid) return;
	const position = teacherList.value.findIndex(item => getTeacherUid(item) === teacherUid) + 1;
	track("mentor_detail_click", {
		mentor_id: teacherUid,
		mentor_type: getMentorTypeByAreaType(teacher?.area_type),
		section_id: "recommended_mentor",
		position,
	});
	toTeacherDetail(teacherUid);
}

function toTeacherDetail(teacherUid) {
	if (!teacherUid) return;
	navigateTo(`/seer/teacher-detail/${teacherUid}`);
}

function toChat(teacherUid) {
	if (!teacherUid) return;

	if (import.meta.client) {
		logEvent("gw_to_free_chat_click", {
			channel: getChannel(),
		});

		if (!localStorage.getItem("token")) {
			localStorage.setItem(TEACHER_UID_STORAGE_KEY, teacherUid);
		}
	}

	const targetUrl = `/seer/chat/${teacherUid}`;

	if (!openLoginPopup) {
		navigateTo(targetUrl);
		return;
	}

	openLoginPopup(() => {
		if (import.meta.client) {
			localStorage.removeItem(TEACHER_UID_STORAGE_KEY);
		}
		navigateTo(targetUrl);
	}, false);

	if (import.meta.client && localStorage.getItem("token")) {
		navigateTo(targetUrl);
	}
}

watch(
	() => pageData.value?.resourceTeachers,
	teachers => {
		if (teachers?.length && !teacherListData.value.length) {
			teacherListData.value = teachers;
			loading.value = false;
			nextTick(() => {
				handleScroll();
			});
		}
	},
	{ immediate: true }
);

onMounted(async () => {
	if (!teacherListData.value.length) {
		await loadTeachers();
	} else {
		loading.value = false;
	}
	syncVisibleCardCount();
	nextTick(() => {
		handleScroll();
		refreshCardObserver();
	});
	if (import.meta.client) {
		window.addEventListener("resize", handleResize);
	}
});

watch(
	() => teacherList.value.length,
	() => {
		nextTick(() => {
			handleScroll();
			refreshCardObserver();
		});
	}
);

const setCardRef = (el, teacher) => {
	const id = getTeacherUid(teacher);
	if (!id) return;
	if (el) cardRefs.set(id, el);
	else cardRefs.delete(id);
};

function refreshCardObserver() {
	if (!import.meta.client) return;
	cardObserver?.disconnect();
	cardObserver = new IntersectionObserver(
		entries => {
			entries.forEach(entry => {
				const mentorId = entry.target.dataset.mentorId;
				if (!mentorId || impressedMentorIds.has(mentorId)) return;
				if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
					if (impressionTimers.has(mentorId)) return;
					const timer = window.setTimeout(() => {
						const teacher = teacherList.value.find(item => getTeacherUid(item) === mentorId);
						if (!teacher || impressedMentorIds.has(mentorId)) return;
						impressedMentorIds.add(mentorId);
						track("recommended_mentor_impression", {
							mentor_id: mentorId,
							mentor_type: getMentorTypeByAreaType(teacher?.area_type),
							position: teacherList.value.findIndex(item => getTeacherUid(item) === mentorId) + 1,
							rating: normalizeMentorRating(teacher?.displayScore),
							consultation_count_bucket: getConsultationCountBucket(teacher?.displayLikes),
						});
					}, 1000);
					impressionTimers.set(mentorId, timer);
				} else if (impressionTimers.has(mentorId)) {
					window.clearTimeout(impressionTimers.get(mentorId));
					impressionTimers.delete(mentorId);
				}
			});
		},
		{ threshold: [0.5] }
	);

	teacherList.value.forEach(teacher => {
		const mentorId = getTeacherUid(teacher);
		const el = cardRefs.get(mentorId);
		if (!el) return;
		el.dataset.mentorId = mentorId;
		cardObserver.observe(el);
	});
}

onBeforeUnmount(() => {
	if (import.meta.client) {
		window.removeEventListener("resize", handleResize);
	}
	impressionTimers.forEach(timer => window.clearTimeout(timer));
	impressionTimers.clear();
	cardObserver?.disconnect();
});
</script>

<style lang="scss" scoped>
.teacher-resource-section {
	--card-gap: 16px;
	--card-cols: 4;
	width: 100%;
	min-width: 0;
	max-width: 100%;
	overflow: visible;

	&__container {
		border-radius: 20px;
		padding: 30px;
		box-shadow: 0 4px 24.6px 0 rgba(0, 0, 0, 0.1);
		margin-top: 30px;
	}

	&__title {
		margin: 0 0 20px;
		font-size: 28px;
		line-height: 60px;
		color: #333333;
	}

	&__status {
		padding: 48px 0;
		text-align: center;
		font-size: 18px;
		line-height: 26px;
		color: #999999;
	}

	&__carousel-wrap {
		overflow: visible;
	}

	&__carousel {
		position: relative;
		width: 100%;
	}

	&__viewport {
		width: 100%;
		overflow: hidden;
	}

	&__list {
		display: flex;
		width: 100%;
		gap: var(--card-gap);
		overflow-x: auto;
		overflow-y: hidden;
		scroll-snap-type: x proximity;
		scroll-behavior: smooth;
		scrollbar-width: none;
		cursor: grab;
		touch-action: pan-x;
		user-select: none;

		&::-webkit-scrollbar {
			display: none;
		}

		&--dragging {
			cursor: grabbing;
			scroll-snap-type: none;
			scroll-behavior: auto;

			img {
				pointer-events: none;
			}
		}
	}

	&__dots {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 8px;
		margin-top: 24px;
	}

	&__dot {
		width: 8px;
		height: 8px;
		padding: 0;
		border: none;
		border-radius: 999px;
		background: #d9d9d9;
		cursor: pointer;
		transition:
			width 0.25s ease,
			background-color 0.25s ease;

		&--active {
			width: 24px;
			background: #f2af19;
		}
	}
}

.teacher-resource-card {
	display: flex;
	flex: 0 0 calc((100% - (var(--card-cols) - 1) * var(--card-gap)) / var(--card-cols));
	width: calc((100% - (var(--card-cols) - 1) * var(--card-gap)) / var(--card-cols));
	flex-direction: column;
	position: relative;
	background: transparent;
	border: none;
	border-radius: 0;
	overflow: visible;
	cursor: pointer;
	scroll-snap-align: start;
	min-width: 0;
	box-sizing: border-box;

	&__media {
		position: relative;
		height: 248px;
		background: #ecf6ff;
		border-radius: 12px 12px 0 0;
		overflow: hidden;
	}

	&__avatar {
		display: block;
		width: 78%;
		height: auto;
		max-height: 92%;
		object-fit: contain;
		object-position: bottom center;
		position: absolute;
		left: 50%;
		bottom: 0;
		transform: translateX(-50%);
		-webkit-user-drag: none;
		user-select: none;
		pointer-events: none;
	}

	&__meta-group {
		position: absolute;
		top: 6px;
		left: 4px;
		display: flex;
		align-items: stretch;
		overflow: hidden;
	}

	&__meta {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 4px;
		height: 24px;
		padding: 0 6px;
		font-size: 14px;
		line-height: 20px;
		white-space: nowrap;
		border-radius: 6px 0px 0px;

		img {
			width: 14px;
			height: 14px;
			flex-shrink: 0;
			-webkit-user-drag: none;
			pointer-events: none;
		}
	}

	&__meta--score {
		background: #ffffff;
		color: #333333;
	}

	&__meta--like {
		background: rgba(0, 0, 0, 0.3);
		color: #ffffff;
		border-radius: 0px 0px 6px 0px;
	}

	&__chat {
		position: absolute;
		right: 16px;
		top: 248px;
		z-index: 2;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 40px;
		height: 40px;
		padding: 0;
		border: none;
		border-radius: 50%;
		background: rgba(49, 49, 49, 0.92);
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
		cursor: pointer;
		transform: translateY(-50%);
		transition: background 0.2s ease;

		img {
			display: block;
			width: 22px;
			height: 22px;
			object-fit: contain;
		}

		&:hover {
			background: #313131;
		}
	}

	&__content {
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: 16px 16px 18px;
		background: #ffffff;
		border: 1px solid #efefef;
		border-top: none;
		border-radius: 0 0 12px 12px;
	}

	&__name {
		margin: 0;
		font-size: 22px;
		line-height: 30px;
		color: #333333;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		font-weight: 500;
	}

	&__badges {
		display: flex;
		flex-wrap: nowrap;
		gap: 8px;
		margin-top: 12px;
		min-height: 26px;
		overflow: hidden;
	}

	&__badge {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		height: 26px;
		padding: 0 10px;
		border-radius: 4px;
		font-size: 12px;
		line-height: 18px;
		white-space: nowrap;
	}

	&__summary-row {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-top: 12px;
		min-height: 66px;
	}

	&__summary {
		flex: 1;
		min-width: 0;
		margin: 0;
		font-size: 14px;
		line-height: 22px;
		color: #666666;
		display: -webkit-box;
		-webkit-line-clamp: 3;
		line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	&__summary-arrow {
		flex-shrink: 0;
		width: 8px;
		height: 8px;
		border-right: 1.5px solid #cccccc;
		border-bottom: 1.5px solid #cccccc;
		transform: rotate(-45deg);
	}

	&__button {
		width: 100%;
		height: 48px;
		margin-top: 14px;
		padding: 0;
		border: 0;
		border-radius: 8px;
		background: #333333;
		font-weight: 500;
		font-size: 20px;
		line-height: 48px;
		color: #ffffff;
		cursor: pointer;
		transition: opacity 0.2s ease;

		&:hover {
			opacity: 0.7;
		}
	}
}

@media (max-width: 1200px) {
	.teacher-resource-section {
		&__dots {
			margin-top: 20px;
		}
	}
}

@media (max-width: 1024px) {
	.teacher-resource-section {
		--card-cols: 3;
		--card-gap: 14px;

		&__title {
			margin: 28px 0;
			font-size: 28px;
			line-height: 42px;
		}
	}

	.teacher-resource-card {
		&__media {
			height: 220px;
		}

		&__chat {
			top: 220px;
		}

		&__name {
			font-size: 20px;
			line-height: 28px;
		}

		&__button {
			height: 44px;
			font-size: 18px;
			line-height: 44px;
		}
	}
}

@media (max-width: 900px) {
	.teacher-resource-section {
		--card-cols: 2;
		--card-gap: 12px;

		&__title {
			margin: 20px 0;
			font-size: 24px;
			line-height: 34px;
		}

		&__carousel-wrap {
			overflow-x: clip;
		}

		&__dots {
			margin-top: 16px;
		}
	}

	.teacher-resource-card {
		&__media {
			height: 200px;
		}

		&__chat {
			right: 12px;
			top: 200px;
			width: 36px;
			height: 36px;

			img {
				width: 18px;
				height: 18px;
			}
		}

		&__content {
			padding: 14px 14px 16px;
		}

		&__name {
			font-size: 18px;
			line-height: 26px;
		}

		&__summary-row {
			min-height: 48px;
		}

		&__summary {
			font-size: 13px;
			line-height: 20px;
			-webkit-line-clamp: 2;
			line-clamp: 2;
		}

		&__button {
			height: 42px;
			font-size: 17px;
			line-height: 42px;
		}
	}
}

@media (max-width: 480px) {
	.teacher-resource-section {
		--card-cols: 1;
	}

	.teacher-resource-card {
		flex: 0 0 100%;
		width: 100%;
	}
}
</style>
